/**
 * Context Menu for Encrypted Content
 * Appears on right-click for authors: read back the sentence that locks a passage,
 * or remove the marking entirely.
 */

import { Key, LockOpen, Public } from '@mui/icons-material'
import { ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material'
import React from 'react'

interface EncryptedContextMenuProps {
  anchorPosition: { top: number; left: number } | null
  onClose: () => void
  onRemoveEncryption: () => void
  /**
   * Offered only for a sentence-locked passage. A character passage has no sentence —
   * its key is never published, and only the character decides whether it opens.
   */
  onEditLockPhrase?: () => void
  /** Offered on a passage the author holds: opening it is the whole point of that kind. */
  onReleaseToEveryone?: () => void
}

export const EncryptedContextMenu: React.FC<EncryptedContextMenuProps> = ({
  anchorPosition,
  onClose,
  onRemoveEncryption,
  onEditLockPhrase,
  onReleaseToEveryone,
}) => {
  const handleRelease = () => {
    onReleaseToEveryone?.()
    onClose()
  }

  const handleEditLockPhrase = () => {
    onEditLockPhrase?.()
    onClose()
  }

  const handleRemoveEncryption = () => {
    onRemoveEncryption()
    onClose()
  }

  return (
    <Menu
      open={anchorPosition !== null}
      onClose={onClose}
      anchorReference="anchorPosition"
      anchorPosition={anchorPosition ?? undefined}
      disableRestoreFocus
    >
      {onReleaseToEveryone && (
        <MenuItem onClick={handleRelease}>
          <ListItemIcon>
            <Public fontSize="small" />
          </ListItemIcon>
          <ListItemText>Open this to everyone</ListItemText>
        </MenuItem>
      )}
      {onEditLockPhrase && (
        <MenuItem onClick={handleEditLockPhrase}>
          <ListItemIcon>
            <Key fontSize="small" />
          </ListItemIcon>
          <ListItemText>The sentence that opens this</ListItemText>
        </MenuItem>
      )}
      <MenuItem onClick={handleRemoveEncryption}>
        <ListItemIcon>
          <LockOpen fontSize="small" />
        </ListItemIcon>
        <ListItemText>Remove Encryption Marking</ListItemText>
      </MenuItem>
    </Menu>
  )
}

export default EncryptedContextMenu
